import { type AxiosRequestConfig, type AxiosResponse } from 'axios'
import { type AxiosOptions } from 'shared/api'
import { baseApi } from 'entities/Auth'
import { type ISubscriptionSeats } from 'entities/Subscription/api/types'
import {
  type IParamsAutorecharge,
  type IParamsUpdateBillingCredits,
  type IResponseBillingAdditionalCreditsPrice,
  type IResponseBillingApplyCoupon,
  type IResponseBillingAutorecharge,
  type IResponseBillingSources,
  type IResponsePlans,
  type IResponseUpdateBillingCredits,
  type IResponseBillingStates,
  type IResponseBillingAreaCodes,
  type IResponseAdditionalCreditPrice,
  type IResponseBillingSource,
  type IParamsCreateSource,
  type IResponseCoupon,
  type IParamsBillingDepositReplenish,
  type IResponseBillingDepositReplenish,
  type IResponseBillingComplianceInfo,
  type IResponseGetBillingPreview,
  type IParamsGetBillingPreview,
  type IParamsAddNumberLicense,
  type IParamsDeleteNumberLicense,
  type IParamsGetBillingPreviewCompliance,
  type IParamsGetPlanData,
  type IParamsUpgradePlan,
  type IParamsBillingSeatsPreview,
  type IParamsRestartSubscription,
  type IResponseBillingPayments,
  type IParamsGetBillingPayments,
  type IResponseBillingPaymentLink,
  type IResponseChurnkeyAuth,
  type IParamsBillingAddress,
  type IResponseBillingAddress,
  type IResponseBillingAddressData,
  type IParamsUpdateSourcesPriority,
  type IParamsBillingPreviewCustomDomain,
} from './types'

class Api {
  getBillingAdditionalCreditPrice(): Promise<
    AxiosResponse<IResponseBillingAdditionalCreditsPrice>
  > {
    return baseApi.get('billing/additional-credit-price')
  }

  getBillingSources(): Promise<AxiosResponse<IResponseBillingSources>> {
    return baseApi.get('core/billing/sources')
  }
  createSource(params: IParamsCreateSource): Promise<AxiosResponse<IResponseBillingSource>> {
    return baseApi.put('core/billing/sources', params)
  }

  applyCoupon(code: string): Promise<AxiosResponse<IResponseBillingApplyCoupon>> {
    return baseApi.post('core/billing/coupons/' + code)
  }

  getBillingAutoRecharge(): Promise<AxiosResponse<IResponseBillingAutorecharge>> {
    // IResponseBillingAutorecharge with price
    return baseApi.get('billing/auto-recharge')
  }

  updateBillingAutoRecharge(
    params: IParamsAutorecharge
  ): Promise<AxiosResponse<IResponseBillingAutorecharge>> {
    // IResponseBillingAutorecharge without price. The problem is on the backend.
    return baseApi.post('core/billing/auto-recharge', params)
  }

  updateBillingCredits(
    params: IParamsUpdateBillingCredits
  ): Promise<AxiosResponse<IResponseUpdateBillingCredits>> {
    return baseApi.post('billing/credits', params)
  }

  getPlans(): Promise<AxiosResponse<IResponsePlans>> {
    return baseApi.get('billing/plans')
  }

  getBillingStates(): Promise<AxiosResponse<IResponseBillingStates>> {
    return baseApi.get('billing/states')
  }

  getBillingAreaCodes(): Promise<AxiosResponse<IResponseBillingAreaCodes>> {
    return baseApi.get('billing/area-codes')
  }
  getAdditionalCreditPrice(): Promise<AxiosResponse<IResponseAdditionalCreditPrice>> {
    return baseApi.get('billing/additional-credit-price')
  }

  getCoupon(coupon: string): Promise<AxiosResponse<IResponseCoupon>> {
    return baseApi.get(`billing/coupons/${coupon}`)
  }

  billingDepositReplenish(
    params: IParamsBillingDepositReplenish
  ): Promise<AxiosResponse<IResponseBillingDepositReplenish>> {
    return baseApi.post('billing/deposit-replenish', params)
  }

  getBillingComplianceInfo(): Promise<AxiosResponse<IResponseBillingComplianceInfo>> {
    return baseApi.get('core/billing/compliance/info')
  }

  updateBillingComplianceInfo(): Promise<AxiosResponse<IResponseBillingComplianceInfo>> {
    return baseApi.post('core/billing/compliance/subscribe')
  }

  getBillingPreview(
    params: IParamsGetBillingPreview,
    options?: AxiosOptions
  ): Promise<AxiosResponse<IResponseGetBillingPreview>> {
    return baseApi.get('billing/preview/shared-inboxes', { params, ...options })
  }

  getBillingPreviewCustomDomain(
    params: IParamsBillingPreviewCustomDomain,
    options?: AxiosOptions
  ): Promise<AxiosResponse<IResponseGetBillingPreview>> {
    return baseApi.get('billing/preview/custom-domain', { params, ...options })
  }

  upgradeBillingCustomDomain(): Promise<AxiosResponse> {
    return baseApi.post('core/billing/custom-domain/subscribe')
  }

  getPlanData(
    config: AxiosRequestConfig<IParamsGetPlanData>
  ): Promise<AxiosResponse<IResponseGetBillingPreview>> {
    return baseApi.get('billing/preview/plan', config)
  }

  upgradePlan(params: IParamsUpgradePlan): Promise<AxiosResponse> {
    return baseApi.post('billing/upgrade/plan', params)
  }

  cancelUpgradePlan(): Promise<AxiosResponse<void>> {
    return baseApi.post('billing/upgrade/plan/cancel')
  }

  addNumberLicense(
    data: IParamsAddNumberLicense
  ): Promise<AxiosResponse<IParamsDeleteNumberLicense>> {
    return baseApi.post('billing/numbers', data)
  }

  deleteNumberLicense(
    params: IParamsDeleteNumberLicense
  ): Promise<AxiosResponse<IParamsDeleteNumberLicense>> {
    return baseApi.delete('billing/numbers', { params })
  }

  getTrustedCallingBilling(
    params: IParamsGetBillingPreviewCompliance,
    options?: AxiosOptions
  ): Promise<AxiosResponse<IResponseGetBillingPreview>> {
    return baseApi.get('billing/preview/compliance', { params, ...options })
  }

  getBillingSeatsPreview(
    params: IParamsBillingSeatsPreview,
    options?: AxiosOptions
  ): Promise<AxiosResponse<IResponseGetBillingPreview>> {
    return baseApi.get('billing/preview/seats', { params, ...options })
  }
  buySeats(add_seats_count: number): Promise<AxiosResponse<ISubscriptionSeats>> {
    return baseApi.post('billing/seats', { add_seats_count })
  }
  removeSeats(delete_seats_count: number): Promise<AxiosResponse<ISubscriptionSeats>> {
    return baseApi.delete('billing/seats', { params: { delete_seats_count } })
  }

  getBillingPayments(
    params?: IParamsGetBillingPayments,
    options?: AxiosOptions
  ): Promise<AxiosResponse<IResponseBillingPayments>> {
    return baseApi.get('core/billing/payments', { params, ...options })
  }

  getBillingPaymentLink(invoiceId: string): Promise<AxiosResponse<IResponseBillingPaymentLink>> {
    return baseApi.get(`billing/payments/link/${invoiceId}`)
  }

  restartSubscription(params: IParamsRestartSubscription): Promise<AxiosResponse> {
    return baseApi.post('billing/restart-canceled', params)
  }

  setSourcePrimary(sourceId: string): Promise<AxiosResponse<void>> {
    return baseApi.post(`core/billing/sources/${sourceId}`, {
      primary: true,
    })
  }

  deleteSource(sourceId: string): Promise<AxiosResponse<void>> {
    return baseApi.delete(`core/billing/sources/${sourceId}`)
  }

  updateSourcesPriority(
    params: IParamsUpdateSourcesPriority
  ): Promise<AxiosResponse<IResponseBillingSources>> {
    return baseApi.post('core/billing/sources/update-priority', params)
  }

  getBillingAddress(): Promise<AxiosResponse<IResponseBillingAddress>> {
    return baseApi.get('billing/billing_address')
  }

  updateBillingAddress(
    params: IParamsBillingAddress
  ): Promise<AxiosResponse<IResponseBillingAddressData>> {
    return baseApi.post('billing/billing_address', params)
  }

  getChurnkeyAuth(): Promise<AxiosResponse<IResponseChurnkeyAuth>> {
    return baseApi.get('billing/churnkey/auth')
  }

  resumeSubscription(): Promise<AxiosResponse<{ resumed: boolean }>> {
    return baseApi.post('billing/resume')
  }
}

export const BillingApi = new Api()
